import React, { useEffect, useMemo } from "react";
import {
  CircleMarker,
  MapContainer,
  TileLayer,
  Tooltip,
  useMap,
} from "react-leaflet";
import L from "leaflet";
import "leaflet.heat";
import "leaflet/dist/leaflet.css";

const DEFAULT_CENTER = [27.7172, 85.3240]; // Kathmandu fallback

const HeatLayer = ({ points }) => {
  const map = useMap();

  const heatPoints = useMemo(() => {
    return (points || [])
      .map((point) => {
        const lat = Number(point.lat);
        const lng = Number(point.lng);

        if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
          return null;
        }

        /*
          We intentionally increase visual intensity here.
          Backend still sends real user points.
          Frontend only makes low-volume staging data visible.
        */
        const weight = Math.max(Number(point.weight || 1) * 6, 6);

        return [lat, lng, weight];
      })
      .filter(Boolean);
  }, [points]);

  useEffect(() => {
    if (!map || heatPoints.length === 0) return undefined;

    const layer = L.heatLayer(heatPoints, {
      radius: 42,
      blur: 28,
      maxZoom: 16,
      minOpacity: 0.45,
      gradient: {
        0.25: "#60a5fa",
        0.5: "#22c55e",
        0.75: "#facc15",
        1.0: "#ef4444",
      },
    });

    layer.addTo(map);

    const bounds = L.latLngBounds(heatPoints.map(([lat, lng]) => [lat, lng]));

    if (bounds.isValid()) {
      map.fitBounds(bounds, {
        padding: [36, 36],
        maxZoom: heatPoints.length === 1 ? 12 : 7,
      });
    }

    return () => {
      map.removeLayer(layer);
    };
  }, [map, heatPoints]);

  return null;
};

const VenueDemandHeatmap = ({ points = [] }) => {
  const validPoints = useMemo(() => {
    return (points || [])
      .map((point) => {
        const lat = Number(point.lat);
        const lng = Number(point.lng);

        if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
          return null;
        }

        return {
          ...point,
          lat,
          lng,
          weight: Number(point.weight || 1),
        };
      })
      .filter(Boolean);
  }, [points]);

  const center = validPoints.length
    ? [validPoints[0].lat, validPoints[0].lng]
    : DEFAULT_CENTER;

  if (validPoints.length === 0) {
    return (
      <div className="venue-demand-heatmap-empty">
        No heatmap data available yet.
      </div>
    );
  }

  return (
    <div className="venue-demand-heatmap">
      <MapContainer
        center={center}
        zoom={11}
        scrollWheelZoom={false}
        className="venue-demand-heatmap-map"
      >
        <TileLayer
          attribution='&copy; OpenStreetMap contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />

        <HeatLayer points={validPoints} />

        {validPoints.map((point, index) => (
          <CircleMarker
            key={`${point.lat}-${point.lng}-${index}`}
            center={[point.lat, point.lng]}
            radius={7}
            pathOptions={{
              color: "#166534",
              fillColor: "#22c55e",
              fillOpacity: 0.85,
              weight: 2,
            }}
          >
            <Tooltip direction="top" offset={[0, -6]} opacity={1}>
              <div>
                <strong>{point.area || "User area"}</strong>
                <br />
                {point.country || "—"}
              </div>
            </Tooltip>
          </CircleMarker>
        ))}
      </MapContainer>

      <div className="venue-demand-heatmap-legend">
        <span className="venue-demand-legend-dot" />
        User/player location
      </div>
    </div>
  );
};

export default VenueDemandHeatmap;